import {
  updateChatPinnedQuestion,
  type ChatPinnedQuestionHost,
} from "./chat/chat-pinned-question.ts";
import { withProgrammaticScroll } from "./chat/with-programmatic-scroll.ts";

/**
 * Distance (px) from the bottom within which the viewport is treated as "at bottom"
 * for the new-messages indicator. Autoscroll is disabled; this only drives the badge.
 */
const AT_BOTTOM_THRESHOLD = 24;

type ScrollHost = {
  updateComplete: Promise<unknown>;
  querySelector: (selectors: string) => Element | null;
  style: CSSStyleDeclaration;
  chatScrollFrame: number | null;
  chatScrollTimeout: number | null;
  chatHasAutoScrolled: boolean;
  chatUserNearBottom: boolean;
  /**
   * JAISIU-3502: scroll-nav visibility state. The arrows are
   * event-driven — they require a real "armed" trigger before they
   * can show, and once auto-hidden they stay hidden until a new
   * trigger arrives (never reappear just because the user is still
   * scrolled away from the bottom). The visible flag drives the
   * template; the armed flag gates whether it can ever flip to
   * true again.
   */
  /** ↑ older: visible flag. UI reads this. */
  chatNewMessagesAbove: boolean;
  /** ↑ older: armed flag. Set by upward-scroll accumulation. */
  chatNavUpArmed: boolean;
  /** ↑ older: accumulated upward scroll px since last reset
   *  (used by the scroll handler to detect "the user scrolled up a
   *  few messages" without requiring them to be at the top). */
  chatNavUpAccumulator: number;
  /** ↑ older: last seen scrollTop, used to compute the delta. */
  chatNavLastScrollTop: number;
  /** JAISIU-3502: false until the first scroll event after
   *  resetChatScrollNav / app boot — the very first event baselines
   *  the scroll position so the first delta is 0 instead of
   *  `currentScrollTop` (which would always look like a downward
   *  scroll of 1500px when the chat thread starts mid-thread and
   *  the operator nudges the scroll wheel). */
  chatNavLastScrollTopInitialized?: boolean;
  /** ↑ older: per-arrow auto-hide timer handle. */
  chatNavUpTimer: number | null;
  /** ↓ newer: visible flag. UI reads this. */
  chatNewMessagesBelow: boolean;
  /** ↓ newer: armed flag. Set by downward-scroll OR a genuinely
   *  new message arriving below the fold. Once cleared by the
   *  auto-hide timer, the next visible=true requires a new armed
   *  event (re-render, layout change, programmatic scroll, etc. do
   *  NOT count). */
  chatNavDownArmed: boolean;
  /** ↓ newer: last seen chat-messages length, used by the message
   *  trigger to detect "this is a new message, not a re-render". */
  chatNavLastMessagesLength: number;
  /** ↓ newer: per-arrow auto-hide timer handle. */
  chatNavDownTimer: number | null;
  logsScrollFrame: number | null;
  logsAtBottom: boolean;
  topbarObserver: ResizeObserver | null;
  /**
   * Current cursor window size used when paging older transcript
   * messages off the gateway. Mirrors `OpenClawApp.chatHistoryDisplayLimit`.
   */
  chatHistoryDisplayLimit?: number;
  /** True while a chat-history fetch is in flight — used to debounce scroll-up loads. */
  chatLoading?: boolean;
  /**
   * Triggered when the operator scrolls up past the threshold and more
   * older messages may be available. Implementation should issue a
   * `chat.history` request with the current `before` cursor and prepend
   * the result; the scroll-preservation logic re-measures the topmost
   * visible row after the prepend. Returns a promise so the scroll
   * handler can ignore further triggers until it resolves.
   */
  loadOlderChatHistory?: () => Promise<unknown> | void;
  // JAISIU-3532 — matches loadOlderChatHistory for the newer direction.
  loadNewerChatHistory?: () => Promise<unknown> | void;
  /**
   * JAISIU-3473 — cursor + guard for upward pagination.
   *  - `chatHistoryOldestLoadedId` — id of the oldest loaded message
   *    (null when no history has been loaded yet).
   *  - `chatHistoryHasMoreOlder` — server says there is more older
   *    content beyond the current cursor.
   *  - `chatHistoryLoadingOlder` — true while an older-load request is
   *    in flight; rapid scroll bursts coalesce into a single fetch.
   *  - `chatProgrammaticScroll` — flag set by the scroll-preservation
   *    logic while adjusting `scrollTop` after a prepend. While set,
   *    the scroll handler does NOT classify the resulting `scroll`
   *    event as a user gesture (this is the contract that keeps the
   *    ↑/↓ chevrons from firing on viewport correction).
   */
  chatHistoryOldestLoadedId?: string | null;
  chatHistoryHasMoreOlder?: boolean;
  chatHistoryLoadingOlder?: boolean;
  // JAISIU-3532 / 3536 — newest-direction mirror of the older pair.
  chatHistoryNewestLoadedId?: string | null;
  chatHistoryHasMoreNewer?: boolean;
  chatHistoryLoadingNewer?: boolean;
  chatHistoryLoadingCentered?: boolean;
  chatProgrammaticScroll?: boolean;
  /**
   * JAISIU-3474 — schedule a debounced save of the topmost visible
   * message id as the persisted scroll anchor for the active
   * session. The scroll handler invokes this on every non-bracket
   * scroll event so the next browser refresh can land the operator
   * on the same message they were last reading. The host owns the
   * IDB write so this hook stays decoupled from storage details.
   */
  scheduleScrollAnchorSave?: () => void;
  /**
   * JAISIU-3474 — the currently persisted anchor for the active
   * session. Read by the scroll handler so it can tell whether the
   * in-memory view has already diverged from what was saved (and the
   * debounced write is pending). Optional — sessions that have never
   * been scrolled have no anchor yet.
   */
  chatScrollAnchor?: import("./storage/idb-session-cache.ts").IdbScrollAnchorEntry | null;
  /**
   * JAISIU-3474 — session key used to scope the persisted scroll
   * anchor. Read by the scroll handler so the saved record cannot
   * bleed across chats if the operator switches tabs between a
   * scroll and the debounced IDB write.
   */
  sessionKey?: string;
};

function pickChatScrollTarget(host: ScrollHost): HTMLElement | null {
  const selectors = [".chat-fleet-main-scroll", ".chat-thread-scroll", ".chat-thread"];
  for (const selector of selectors) {
    const container = host.querySelector(selector) as HTMLElement | null;
    if (!container) {
      continue;
    }
    const overflowY = getComputedStyle(container).overflowY;
    const canScroll =
      overflowY === "auto" ||
      overflowY === "scroll" ||
      container.scrollHeight - container.clientHeight > 1;
    if (canScroll) {
      return container;
    }
  }
  return (document.scrollingElement ?? document.documentElement) as HTMLElement | null;
}

function centerIntersectsRow(center: number, top: number, bottom: number): boolean {
  return center >= top && center <= bottom;
}

function clampSection(value: number): 0 | 1 | 2 | 3 | 4 {
  if (!Number.isFinite(value)) return 2;
  const v = Math.round(value);
  if (v <= 0) return 0;
  if (v >= 4) return 4;
  return v as 0 | 1 | 2 | 3 | 4;
}

/**
 * JAISIU-3533-update — snapshot the message that the operator is
 * currently READING (the one that intersects the viewport CENTER),
 * plus the fractional position of the viewport center within that
 * message. Restore mirrors this so a long answer the operator
 * was reading at section 4/5 lands back on section 4/5 — not on
 * the message top edge.
 *
 * Why the viewport CENTER instead of the top:
 *
 *   The reading-focus point is what the operator's eyes are on, and
 *   that is almost never the top edge of the chat scroll container.
 *   For a viewport showing rows A..C, the operator is reading B (the
 *   middle row), not the top of A. Anchoring on the top edge placed
 *   the row at the top of the viewport on restore — which meant
 *   section 4/5 of a long answer dropped back to section 1/5, and
 *   the operator scrolled to find their place again.
 *
 * Why fractional position + a 5-bucket sectionIndex:
 *
 *   The fractional position (`positionRatio`, 0..1) gives pixel-precise
 *   restoration. The 5-bucket sectionIndex is a coarse safety net
 *   used when the row's post-restore height differs materially from
 *   the captured height (e.g. an image or code block has not finished
 *   streaming yet): the restore falls back to placing the bucket
 *   boundary at the viewport center instead of using the floating
 *   ratio. Both are stored; the restore prefers `positionRatio`.
 *
 *   Boundary fallback when no row intersects the viewport center
 *   (very short transcripts or the operator sitting between two rows):
 *   we use the row whose offsetTop is at-or-above the center (the
 *   row the operator is reading), falling back to the first row in
 *   the DOM when even that is past the center.
 */
function captureScrollAnchor(host: ScrollHost, container: HTMLElement): void {
  const sessionKey = (host.sessionKey ?? "").trim();
  if (!sessionKey) return;
  const rows = container.querySelectorAll<HTMLElement>("[data-msg-id]");
  if (rows.length === 0) return;
  const scrollTop = container.scrollTop;
  const clientHeight = container.clientHeight || 0;
  // The vertical CENTER of the viewport — the reading-focus point.
  const viewportCenter = scrollTop + clientHeight / 2;
  // Pick the message whose vertical band [offsetTop, offsetTop + h]
  // contains the viewport center. If no row is intersected (the
  // operator is reading the very bottom edge of the transcript
  // where the DOM has not yet paginated), fall back to the row
  // whose offsetTop is the closest at-or-above the center (the
  // topmost visible row), so capture still tracks the operator's
  // reading position rather than guessing the message after the
  // gap.
  let chosen: HTMLElement | null = null;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const top = row.offsetTop;
    const bottom = top + (row.offsetHeight || 1);
    if (centerIntersectsRow(viewportCenter, top, bottom)) {
      chosen = row;
      break;
    }
    if (top > viewportCenter) {
      // Past the center; the row above is the closest match if we
      // did not already pick anything.
      if (!chosen) chosen = rows[Math.max(0, i - 1)] ?? row;
      break;
    }
    chosen = row;
  }
  if (!chosen) chosen = rows[0];
  const messageId = chosen.getAttribute("data-msg-id");
  if (!messageId) return;
  const rowHeight = Math.max(1, chosen.offsetHeight || 1);
  // Fractional position of the viewport center WITHIN the message,
  // clamped to [0, 1]. The restore mirrors this so we land at the
  // same proportional point.
  const positionRatio = Math.max(
    0,
    Math.min(1, (viewportCenter - chosen.offsetTop) / rowHeight),
  );
  // 5-bucket quantisation: 0%, 20%, 40%, 60%, 80-100%. Stored
  // alongside `positionRatio` so the restore can short-circuit to
  // a bucketed placement when the row's final height at restore
  // time differs from capture (e.g. an image not yet loaded).
  const sectionIndex = clampSection(Math.round(positionRatio * 4));
  // Legacy field — kept for back-compat with older IDB readers.
  // `viewportOffset` is the px from the row top to the viewport
  // TOP (not center); derived so older restore code paths that
  // read it still produce something sensible.
  const viewportTopWithinRow = Math.max(
    0,
    Math.min(rowHeight - 1, Math.floor(scrollTop - chosen.offsetTop)),
  );
  const current = host.chatScrollAnchor as
    | {
        sessionKey: string;
        messageId: string;
        positionRatio: number;
        sectionIndex: number;
        viewportHeight: number;
        viewportOffset: number;
        capturedAt: number;
        transcriptRevision: string;
      }
    | null
    | undefined;
  if (
    current &&
    current.sessionKey === sessionKey &&
    current.messageId === messageId &&
    Math.abs((current.positionRatio ?? -1) - positionRatio) < 0.005 &&
    current.sectionIndex === sectionIndex &&
    current.viewportHeight === clientHeight
  ) {
    // No-op when nothing changed — debounce stays armed.
    return;
  }
  const capturedAt = Date.now();
  const revision =
    typeof (host as unknown as { chatScrollTranscriptRevision?: string })
      .chatScrollTranscriptRevision === "string"
      ? (host as unknown as { chatScrollTranscriptRevision: string })
          .chatScrollTranscriptRevision
      : String(capturedAt);
  const next = {
    sessionKey,
    messageId,
    viewportOffset: viewportTopWithinRow,
    positionRatio,
    sectionIndex,
    viewportHeight: clientHeight,
    capturedAt,
    transcriptRevision: revision,
  };
  (host as unknown as { chatScrollAnchor: unknown }).chatScrollAnchor = next;
  // JAISIU-3533 — keep the per-session map in sync so a cache-hit
  // restore (activateChatSession fast path) can read without a
  // round-trip to IDB. We assign a new Map instance so Lit picks
  // up the reference change.
  const mapHost = host as unknown as {
    chatScrollAnchorBySession?: Map<
      string,
      {
        sessionKey: string;
        messageId: string;
        viewportOffset: number;
        capturedAt: number;
        transcriptRevision: string;
      }
    >;
  };
  if (mapHost.chatScrollAnchorBySession) {
    const nextMap = new Map(mapHost.chatScrollAnchorBySession);
    nextMap.set(sessionKey, next);
    mapHost.chatScrollAnchorBySession = nextMap;
  }
  if (typeof host.scheduleScrollAnchorSave === "function") {
    host.scheduleScrollAnchorSave();
  }
}

function cancelChatScrollWork(host: ScrollHost): void {
  if (host.chatScrollFrame) {
    cancelAnimationFrame(host.chatScrollFrame);
    host.chatScrollFrame = null;
  }
  if (host.chatScrollTimeout != null) {
    clearTimeout(host.chatScrollTimeout);
    host.chatScrollTimeout = null;
  }
}

/**
 * After chat content changes: update the "new messages below" badge and pinned
 * question. NEVER mutates the transcript scroll position. Autoscroll was
 * removed because it fought the operator's reading position during streaming,
 * session switches, and — most painfully — when an inline Activity bubble was
 * expanded mid-frame. In that case content grew above the bottom, the user's
 * viewport was momentarily not pinned, and any snap-to-bottom logic would
 * collapse the bubble they had just opened and yank them away from it.
 *
 * This function only flips the "new messages below" badge. The user clicks
 * the badge (or presses End) to jump to the latest message.
 *
 * The unused `force` parameter is kept so existing call sites compile unchanged.
 */
export function scheduleChatScroll(host: ScrollHost, _force = false) {
  cancelChatScrollWork(host);
  void host.updateComplete.then(() => {
    host.chatScrollFrame = requestAnimationFrame(() => {
      host.chatScrollFrame = null;
      const target = pickChatScrollTarget(host);
      if (!target) {
        return;
      }
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      const atBottom = distanceFromBottom < AT_BOTTOM_THRESHOLD;
      host.chatUserNearBottom = atBottom;
      // JAISIU-3502: scheduleChatScroll is called on every chat content
      // change (streaming tokens, layout resize, role swap, etc.). It
      // is NOT a "new message arrived" event and must NOT arm the
      // ↓ chevron. The only thing this handler does for scroll-nav
      // visibility is:
      //   - if the operator is now at the bottom, hide any visible
      //     down-arrow (the rendered content caught up with the view)
      //   - if the down-arrow is armed+visible because a truly new
      //     message arrived (chatNavDownArmed was set by
      //     noteChatMessagesGrew), leave it alone — the operator is
      //     not at the bottom and the new content is still below the
      //     fold.
      // Symmetrically, if a re-render changes scrollHeight in a way
      // that moves the viewport further from the bottom (e.g. a
      // long code block expanded above the viewport), this handler
      // does NOT arm the chevron — the operator has to perform a
      // new downward scroll or a new message has to actually arrive.
      if (atBottom && host.chatNewMessagesBelow) {
        hideDownArrow(host);
      }
      // NEVER mutate scrollTop here. Any snap-to-bottom would collapse
      // inline Activity bubbles the operator has just expanded and yank
      // them away from the line they were reading. Users who actually want
      // to jump to the latest message use the "new messages below" badge
      // (scrollChatToBottom) or press End.
      updateChatPinnedQuestionIfSupported(host, target);
    });
  });
}

/**
 * Explicit one-shot jump to the latest messages (e.g. "New messages" button).
 * Does not enable follow-mode; subsequent content updates will not auto-scroll.
 */
export function scrollChatToBottom(host: ScrollHost): void {
  cancelChatScrollWork(host);
  // JAISIU-3534 — bracket the scroll write through
  // `withProgrammaticScroll` so the post-write scroll event does
  // NOT arm the ↑/↓ chevrons at the wrong moment. Without this
  // bracket, the chevron state machine treats the auto-scroll as a
  // user gesture and could arm ↑ right after the operator asked
  // for "back to bottom."
  void host.updateComplete.then(() => {
    void withProgrammaticScroll(host as unknown as Parameters<typeof withProgrammaticScroll>[0], async () => {
      host.chatScrollFrame = requestAnimationFrame(() => {
        host.chatScrollFrame = null;
        const target = pickChatScrollTarget(host);
        if (!target) {
          return;
        }
        target.scrollTop = target.scrollHeight;
        host.chatHasAutoScrolled = true;
        host.chatUserNearBottom = true;
        hideDownArrow(host);
        host.chatNavUpAccumulator = 0;
        if (host.chatNewMessagesAbove) {
          hideUpArrow(host);
        }
        updateChatPinnedQuestionIfSupported(host, target);
      });
    });
  });
}

function updateChatPinnedQuestionIfSupported(
  host: ScrollHost,
  scrollRoot: HTMLElement | null,
): void {
  if (!scrollRoot || !("chatPinnedUserQuestion" in host)) {
    return;
  }
  updateChatPinnedQuestion(host as ScrollHost & ChatPinnedQuestionHost, scrollRoot);
}

export function scheduleLogsScroll(host: ScrollHost, force = false) {
  if (host.logsScrollFrame) {
    cancelAnimationFrame(host.logsScrollFrame);
  }
  void host.updateComplete.then(() => {
    host.logsScrollFrame = requestAnimationFrame(() => {
      host.logsScrollFrame = null;
      const container = host.querySelector(".log-stream") as HTMLElement | null;
      if (!container) {
        return;
      }
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      const shouldStick = force || distanceFromBottom < 80;
      if (!shouldStick) {
        return;
      }
      void withProgrammaticScroll(host as unknown as Parameters<typeof withProgrammaticScroll>[0], async () => {
        container.scrollTop = container.scrollHeight;
      });
    });
  });
}

/**
 * Distance (px) from the top within which the viewport is treated as
 * "scrolled to the top" for the older-messages pager. JAISIU-3532
 * widened this from 80 to 200 px so a normal interactive read does
 * NOT keep firing the pager on touchpads / smooth-scroll wheels —
 * the operator has to genuinely approach the top of the rendered
 * window before the cursor kicks in. The scroll-nav ↑ chevron
 * arming threshold (UP_TRIGGER_DELTA_PX) is a separate concern and
 * remains at 80 px.
 */
const AT_TOP_THRESHOLD = 200;

// JAISIU-3473 — cursor-based upward pagination replaces the old
// `chatHistoryDisplayLimit` sliding-window approach. The hard cap that
// used to gate the scroll-up pager (`CHAT_HISTORY_HARD_MAX = 1000`)
// is now redundant: the client arms/stops the pager from the server's
// explicit `hasMoreOlder` boolean. Keeping the constant here would
// just be dead code; the loader enforces the cap via `limit` on the
// RPC instead.

/* ───── JAISIU-3502 — chat scroll-nav visibility state machine ───── */

/**
 * Distance (px) the operator must scroll upward in accumulated deltas
 * before the ↑ chevron is armed. Sized to match "a small deliberate
 * upward swipe" — about one tall message row (~80px). Anything
 * smaller than this and the arrow would flash on every trackpad
 * jitter; anything larger and the chevron does not appear during
 * normal interactive reading.
 */
const UP_TRIGGER_DELTA_PX = 80;

/**
 * Distance (px) the operator must scroll downward in accumulated
 * deltas (while not at the bottom) before the ↓ chevron is armed.
 * Matched to UP_TRIGGER_DELTA_PX so the two arrows behave
 * symmetrically.
 */
const DOWN_TRIGGER_DELTA_PX = 200;

/**
 * JAISIU-3532 — distance (px) from the rendered bottom within which
 * the viewport is treated as "scrolled to the bottom" for the
 * newer-history pager trigger. The newer pager only fires when a
 * saved anchor exists (the no-anchor / newest-at-bottom case is
 * owned by polling + streaming, see JAISIU-3535). 400 px gives a
 * comfortable buffer so a deliberate scroll toward the bottom
 * triggers one fetch, not several. Distinct from
 * `AT_BOTTOM_THRESHOLD` above (which is the new-messages badge
 * indicator at 24 px).
 */
const NEWER_PAGER_BOTTOM_PX = 400;

/**
 * JAISIU-3532 — minimum visible duration (ms) for the older + newer
 * loader indicators. A sub-200ms RPC that flashes the indicator for
 * a single frame is worse UX than no indicator at all (the operator
 * assumes the fetch failed and tries again). The wall-clock gate is
 * enforced in the loader hide path.
 */
const LOADER_MIN_VISIBLE_MS = 200;

/**
 * Milliseconds of scroll inactivity after which an armed+visible
 * arrow auto-hides. Restarts on every scroll event that genuinely
 * moves the viewport. Picked at ~2.4s so the operator has time to
 * read the arrow and decide, but it goes away before it becomes
 * visual noise. Identical for both arrows.
 */
const SCROLL_NAV_AUTO_HIDE_MS = 2400;

/* eslint-disable @typescript-eslint/no-explicit-any */
function getWindow(): any {
  if (typeof window !== "undefined") return window;
  // Stub for the node test environment. setTimeout fires the
  // callback asynchronously (next microtask) so tests can observe
  // the pre-fire state; clearTimeout cancels a pending callback.
  // Tests that want to fire timers synchronously can call
  // `flushChatScrollNavTimers()` below.
  return {
    setTimeout: (fn: () => void) => {
      const handle = setTimeout(fn, 0) as unknown as number;
      pendingTimers.push(fn);
      pendingHandles.set(fn, handle);
      return handle;
    },
    clearTimeout: (handle: number) => {
      for (const [fn, h] of pendingHandles.entries()) {
        if (h === handle) {
          pendingTimers = pendingTimers.filter((f) => f !== fn);
          pendingHandles.delete(fn);
          clearTimeout(handle as unknown as ReturnType<typeof setTimeout>);
          return;
        }
      }
    },
  };
}

/* Node-test plumbing. The above stub defers timer callbacks so a
 * test can advance state mid-cycle. Pending callbacks live in
 * `pendingTimers` so the test can flush them explicitly. */
let pendingTimers: Array<() => void> = [];
let pendingHandles: Map<() => void, number> = new Map();
export function flushChatScrollNavTimers(): void {
  const toFlush = pendingTimers;
  pendingTimers = [];
  for (const fn of toFlush) {
    pendingHandles.delete(fn);
    fn();
  }
}
export function pendingChatScrollNavTimerCount(): number {
  return pendingTimers.length;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

function hideUpArrow(host: ScrollHost): void {
  if (host.chatNavUpTimer != null) {
    getWindow().clearTimeout(host.chatNavUpTimer);
    host.chatNavUpTimer = null;
  }
  host.chatNewMessagesAbove = false;
  // Once hidden by the timer, the up-arrow must require a fresh
  // user-scroll event to reappear. We clear the armed flag so that
  // the next render / layout / focus event cannot re-trigger it.
  host.chatNavUpArmed = false;
  host.chatNavUpAccumulator = 0;
}

function hideDownArrow(host: ScrollHost): void {
  if (host.chatNavDownTimer != null) {
    getWindow().clearTimeout(host.chatNavDownTimer);
    host.chatNavDownTimer = null;
  }
  host.chatNewMessagesBelow = false;
  // CRITICAL: clear the armed flag here. The whole point of the
  // JAISIU-3502 fix — without this clear, every subsequent
  // scheduleChatScroll (which fires on every chat update) would
  // re-arm the arrow because the user is "still not at the bottom".
  // Once we have auto-hidden, the user must perform a new downward
  // scroll OR a new genuinely-new message must arrive for the arrow
  // to come back.
  host.chatNavDownArmed = false;
}

function scheduleUpAutoHide(host: ScrollHost): void {
  if (host.chatNavUpTimer != null) {
    getWindow().clearTimeout(host.chatNavUpTimer);
  }
  host.chatNavUpTimer = getWindow().setTimeout(() => {
    host.chatNavUpTimer = null;
    hideUpArrow(host);
  }, SCROLL_NAV_AUTO_HIDE_MS);
}

function scheduleDownAutoHide(host: ScrollHost): void {
  if (host.chatNavDownTimer != null) {
    getWindow().clearTimeout(host.chatNavDownTimer);
  }
  host.chatNavDownTimer = getWindow().setTimeout(() => {
    host.chatNavDownTimer = null;
    hideDownArrow(host);
  }, SCROLL_NAV_AUTO_HIDE_MS);
}

/**
 * Public hook: reset both arrows (visibility + armed + timers) and
 * clear any accumulated scroll state. Call on session switch and on
 * disconnect so a new chat does not inherit the previous chat's
 * arrow state.
 */
export function resetChatScrollNav(host: ScrollHost): void {
  hideUpArrow(host);
  hideDownArrow(host);
  host.chatNavUpAccumulator = 0;
  host.chatNavLastScrollTop = 0;
  host.chatNavLastScrollTopInitialized = false;
  host.chatNavLastMessagesLength = 0;
}

/**
 * Public hook: notify the controller that the chat transcript grew
 * by exactly one message (or by N — pass the new length). If the
 * operator is not at the bottom, the new message below the fold
 * arms + shows the ↓ chevron (this is the ONLY legitimate
 * non-scroll trigger).
 *
 * Must be called from the place where `chatMessages` is assigned,
 * not from `scheduleChatScroll`, so the trigger cannot be muddied by
 * re-renders, layout changes, or programmatic scrolls.
 */
export function noteChatMessagesGrew(host: ScrollHost, newLength: number): void {
  const prev = host.chatNavLastMessagesLength | 0;
  host.chatNavLastMessagesLength = newLength;
  // Strictly increasing length is the only valid "new message"
  // signal — equal length means a re-render of the same content
  // (e.g. status update, role swap, scroll restore) and must NOT
  // arm the down-arrow.
  if (newLength <= prev) return;
  // If the user is already at the bottom, no chevron needed.
  if (host.chatUserNearBottom) return;
  host.chatNavDownArmed = true;
  host.chatNewMessagesBelow = true;
  scheduleDownAutoHide(host);
}

export function handleChatScroll(host: ScrollHost, event: Event) {
  const container = event.currentTarget as HTMLElement | null;
  if (!container) {
    return;
  }
  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
  host.chatUserNearBottom = distanceFromBottom < AT_BOTTOM_THRESHOLD;

  // ── JAISIU-3502 — scroll-nav state machine ──
  //
  // The operator moved the viewport. We compute a delta from the
  // last scroll position, classify it as up / down / sideways, and
  // feed it into the per-arrow armed flags. Only genuine UP / DOWN
  // deltas change anything; tiny accidental ticks (≤1px) are
  // filtered so the arrows don't flash. The down-arrow's visibility
  // is gated by its armed flag so a re-render that fires
  // scheduleChatScroll cannot resurrect a hidden arrow (that was
  // the old behavior).

  const scrollTop = container.scrollTop;
  // JAISIU-3502: baseline the previous scrollTop on the very first
  // event after a reset. Without this, a chat thread that opens
  // mid-scroll (chatNavLastScrollTop === 0 by default) would classify
  // the first scrollTop=1500 event as a 1500px DOWNWARD jump and
  // arm ↓ instead of recognizing the operator's first upward
  // nudge. This flag is reset by resetChatScrollNav on session
  // switch so each new chat re-baselines correctly.
  if (!host.chatNavLastScrollTopInitialized) {
    host.chatNavLastScrollTop = scrollTop;
    host.chatNavLastScrollTopInitialized = true;
updateChatPinnedQuestionIfSupported(host, container);
    // If we open directly at the bottom (e.g. on a fresh chat whose
    // tail was already in view) the badge should be cleared immediately
    // — there is no "new" message below the fold because the operator
    // never left the bottom.
    if (host.chatUserNearBottom && host.chatNewMessagesBelow) {
      hideDownArrow(host);
    }
    // Skip up/down delta classification for this event. We still
    // honor the scroll-up pager (at-top threshold) so a chat that
    // opens already at the top can still page older messages.
    //
    // JAISIU-3473 — the pager is gated on the new cursor fields:
    //  - `chatHistoryHasMoreOlder` — the server said there is more.
    //  - `chatHistoryLoadingOlder` — no other request is in flight.
    //  - `chatHistoryOldestLoadedId` — we have a cursor to send back.
    if (
      scrollTop < AT_TOP_THRESHOLD &&
      host.loadOlderChatHistory &&
      !host.chatHistoryLoadingOlder &&
      host.chatHistoryHasMoreOlder &&
      host.chatHistoryOldestLoadedId &&
      !host.chatProgrammaticScroll
    ) {
      void host.loadOlderChatHistory();
    }
    return;
  }
  // JAISIU-3473 — the scroll-preservation logic adjusts scrollTop
  // after every prepend and brackets the write with
  // `chatProgrammaticScroll` so the resulting `scroll` event is NOT
  // classified as a user gesture. The flag is set inside
  // `restoreScrollAnchor` for two animation frames (one to commit
  // the Lit re-render, one to let the scroll handler observe the
  // flag set BEFORE the scroll event fires). Without this guard,
  // every prepend would either arm the ↑ chevron (because the
  // delta looks like a giant upward scroll of N pixels) or the
  // ↓ chevron (if the post-prepend scrollTop lands below the
  // pre-fetch scrollTop due to round-off in the anchor math).
  if (host.chatProgrammaticScroll) {
    // Update the baseline so the NEXT genuine user gesture sees a
    // sensible delta; do not run the up/down classification while
    // the programmatic adjustment is in flight.
    host.chatNavLastScrollTop = scrollTop;
    host.chatUserNearBottom = distanceFromBottom < AT_BOTTOM_THRESHOLD;
    return;
  }
  const prevScrollTop = host.chatNavLastScrollTop;
  const delta = scrollTop - prevScrollTop;
  host.chatNavLastScrollTop = scrollTop;

  // Up direction: viewport moved toward older content (scrollTop
  // decreased). Accumulate until the threshold is reached, then arm
  // + show the ↑ chevron (even if the operator is far from the very
  // top — the operator spec is "regardless of where in the thread
  // the upward scrolling starts").
  if (delta < -1) {
    host.chatNavUpAccumulator = (host.chatNavUpAccumulator || 0) + Math.abs(delta);
    if (host.chatNavUpAccumulator >= UP_TRIGGER_DELTA_PX) {
      host.chatNavUpArmed = true;
      host.chatNewMessagesAbove = true;
      // Auto-hide after inactivity — but while the operator is
      // still actively scrolling up the timer must restart.
      scheduleUpAutoHide(host);
    }
  } else if (delta > 1) {
    // Downward scroll: if the operator was previously away from the
    // bottom (down-arrow was potentially visible) then a new
    // downward scroll is a legitimate "I'm coming back" signal.
    // Re-arm the down-arrow so the next auto-hide cycle can show
    // it. This still respects the event-driven rule: the arrow
    // can only become visible if a real downward scroll happened
    // — never just because the container re-rendered.
    if (!host.chatUserNearBottom) {
      host.chatNavDownArmed = true;
      host.chatNewMessagesBelow = true;
      scheduleDownAutoHide(host);
    }
    // Reset the upward accumulator so back-and-forth scrolling
    // does not arm the ↑ chevron by accident.
    host.chatNavUpAccumulator = 0;
  }

  // If the operator scrolls back to the bottom, both arrows fade
  // out immediately — no point showing either control.
  if (host.chatUserNearBottom) {
    if (host.chatNewMessagesBelow) hideDownArrow(host);
    if (host.chatNavUpArmed) {
      // Don't un-arm ↑ here; the operator might scroll up again.
      // Just hide the visible flag, keep the accumulated state so
      // a subsequent upward scroll re-shows without re-accumulating
      // from scratch. The auto-hide timer keeps running.
      host.chatNewMessagesAbove = false;
      if (host.chatNavUpTimer != null) {
        getWindow().clearTimeout(host.chatNavUpTimer);
        host.chatNavUpTimer = null;
      }
    }
  }
  // JAISIU-3474 — capture the topmost visible message id as the
  // persisted scroll anchor. The handler defers the actual IDB
  // write to the host (`scheduleScrollAnchorSave`) so we don't
  // hammer IndexedDB on every scroll event. Skipped during the
  // `chatProgrammaticScroll` bracket — programmatic viewport
  // corrections (older-page prepends, restore on mount) are not
  // "where the operator stopped reading" and must not overwrite the
  // anchor they were trying to land on.
  if (!host.chatProgrammaticScroll && host.scheduleScrollAnchorSave) {
    captureScrollAnchor(host, container);
  }
  // JAISIU-3473 — scroll-up pager.
  //
  // When the operator scrolls toward the top of the loaded transcript,
  // request the next older page from the gateway. The response is
  // prepended by `loadOlderChatHistory` and the scroll position is
  // preserved by anchoring on the topmost visible row. We never
  // mutate scroll position here; that lives entirely in
  // `restoreScrollAnchor`.
  //
  // Guards:
  //   - host must expose the load callback (Fleet Control wires it)
  //   - must not already be loading older history (debounces wheel
  //     storms; the in-flight guard is owned by the loader, not the
  //     scroll handler, so this check is the gate we enforce here)
  //   - the server must have signalled there is more older content
  //   - we must have a cursor to send back
  //   - we must NOT be in a programmatic-scroll bracket, or the
  //     post-prepend viewport correction would itself re-trigger the
  //     pager in a feedback loop.
  // Programmatic-scroll bracket guard applies to BOTH directions.
  if (host.chatProgrammaticScroll) {
    return;
  }
  // ── Older-pager trigger ──────────────────────────────────────────
  // Fires when the operator scrolls up near the top. Wrapped in its
  // own block so the function does NOT early-return on the older
  // path — the newer-pager trigger below must also run.
  if (scrollTop < AT_TOP_THRESHOLD) {
    if (
      host.loadOlderChatHistory &&
      !host.chatHistoryLoadingOlder &&
      host.chatHistoryHasMoreOlder &&
      host.chatHistoryOldestLoadedId
    ) {
      void host.loadOlderChatHistory();
    }
  }

  // ── JAISIU-3532 — newer-pager trigger ────────────────────────────
  // Fires when:
  //   - a saved anchor exists for the active session (otherwise the
  //     newest-at-bottom case is canonical and polling owns arrival)
  //   - the rendered bottom is within NEWER_PAGER_BOTTOM_PX of the
  //     viewport bottom
  //   - the host exposes loadNewerChatHistory
  //   - no newer-pager request is in flight
  //   - we have a newest cursor to send back
  const distFromBottom = container.scrollHeight - container.clientHeight - scrollTop;
  if (distFromBottom < NEWER_PAGER_BOTTOM_PX) {
    const anchor = host.chatScrollAnchor as { sessionKey?: string } | null | undefined;
    const sessionKey =
      (host as unknown as { sessionKey?: string }).sessionKey ?? "";
    if (
      anchor &&
      anchor.sessionKey === sessionKey.trim() &&
      host.loadNewerChatHistory &&
      !host.chatHistoryLoadingNewer &&
      host.chatHistoryHasMoreNewer &&
      host.chatHistoryNewestLoadedId
    ) {
      void host.loadNewerChatHistory();
    }
  }
}

export function handleLogsScroll(host: ScrollHost, event: Event) {
  const container = event.currentTarget as HTMLElement | null;
  if (!container) {
    return;
  }
  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
  host.logsAtBottom = distanceFromBottom < 80;
}

export function resetChatScroll(host: ScrollHost) {
  host.chatHasAutoScrolled = false;
  host.chatUserNearBottom = false;
  // JAISIU-3502: resetChatScroll fires on session switch + app
  // disconnect. Wipe both arrows' visibility, armed flags, accumulators,
  // and timers so the next chat starts from a clean slate.
  resetChatScrollNav(host);
  if ("chatPinnedUserQuestion" in host) {
    (host as ScrollHost & ChatPinnedQuestionHost).chatPinnedUserQuestion = null;
  }
}

export function exportLogs(lines: string[], label: string) {
  if (lines.length === 0) {
    return;
  }
  const blob = new Blob([`${lines.join("\n")}\n`], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  anchor.href = url;
  anchor.download = `openclaw-logs-${label}-${stamp}.log`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function observeTopbar(host: ScrollHost) {
  if (typeof ResizeObserver === "undefined") {
    return;
  }
  const topbar = host.querySelector(".topbar");
  if (!topbar) {
    return;
  }
  const update = () => {
    const { height } = topbar.getBoundingClientRect();
    host.style.setProperty("--topbar-height", `${height}px`);
  };
  update();
  host.topbarObserver = new ResizeObserver(() => update());
  host.topbarObserver.observe(topbar);
}
